import {
  type Aabb2,
  type MarqueeMergeMode,
  type MarqueeState,
  type Point2,
  type SelectionRef,
  type Store,
  abortMarquee,
  beginMarquee,
  inflateAabb,
  isMarqueeLive,
  marqueeMergeMode,
  marqueeRect,
  moveMarquee,
  setMarqueeAlt,
  setMarqueeSpace,
} from '@frontend/viewer-core';

import { MARQUEE_TOLERANCE_PX } from '../constants';
import type {
  DrawingModel,
  MarqueeController,
  MarqueePreview,
  SelectionManager,
} from './types';

type Dependencies = {
  marqueeStore: Store<MarqueeState | null>;
  /** What the box would collect right now. Live tier — the overlay reads it. */
  previewStore: Store<MarqueePreview | null>;
  model: () => DrawingModel | null;
  getHiddenLayers: () => ReadonlySet<number>;
  selection: SelectionManager;
  screenToWorld: (screen: Point2) => Point2;
};

/**
 * Maps a screen-space box onto a world-space one.
 *
 * Exact, not approximate: view rotation is restricted to quarter turns, which map
 * axes onto axes, so two opposite corners are enough to recover the world box. A
 * free rotation angle would make this a rotated rectangle and force the query
 * itself to change shape — which is a large part of why rotation is quarter turns.
 */
const screenRectToWorld = (
  rect: Aabb2,
  screenToWorld: (screen: Point2) => Point2,
): Aabb2 => {
  const [x0, y0] = screenToWorld([rect[0], rect[1]]);
  const [x1, y1] = screenToWorld([rect[2], rect[3]]);
  return [
    Math.min(x0, x1),
    Math.min(y0, y1),
    Math.max(x0, x1),
    Math.max(y0, y1),
  ];
};

/** True when two previews would paint the same thing. */
const isSamePreview = (
  a: MarqueePreview | null,
  b: MarqueePreview | null,
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b || a.mode !== b.mode || a.loopIds.length !== b.loopIds.length) {
    return false;
  }
  return a.loopIds.every((loopId, index) => loopId === b.loopIds[index]);
};

export const createMarqueeController = ({
  marqueeStore,
  previewStore,
  model,
  getHiddenLayers,
  selection,
  screenToWorld,
}: Dependencies): MarqueeController => {
  /**
   * The loops the box has caught, under the rule the gesture locked in.
   *
   * Shared by the live preview and the release, deliberately: a preview computed
   * even slightly differently from the commit would show the user one thing and
   * then do another, which is worse than showing nothing at all.
   */
  const collect = (current: MarqueeState): number[] => {
    const drawing = model();
    if (!drawing || current.isAborted || !isMarqueeLive(current)) {
      return [];
    }

    // Inflated in SCREEN space before the conversion, so the forgiveness is the
    // same few pixels at every zoom level and quarter turn rather than a world
    // distance that balloons when zoomed out.
    const relations = drawing.index.queryRect(
      screenRectToWorld(
        inflateAabb(marqueeRect(current), MARQUEE_TOLERANCE_PX),
        screenToWorld,
      ),
      { hiddenLayers: getHiddenLayers() },
    );

    const caught: number[] = [];
    relations.forEach((relation, loopId) => {
      // A right-to-left box takes anything it touches — but never the thing it
      // is sitting inside, which would otherwise grab the part outline every
      // time a user boxed a feature in the middle of it.
      const isCaught =
        current.rule === 'crossing'
          ? relation === 'enclosed' || relation === 'crossing'
          : relation === 'enclosed';
      if (isCaught) {
        caught.push(loopId);
      }
    });

    // Sorted so an unchanged catch compares equal regardless of the order the
    // spatial query happened to visit its buckets in.
    return caught.sort((a, b) => a - b);
  };

  /**
   * Republishes what the box is about to do.
   *
   * Runs on every pointer move, but only *publishes* when the catch actually
   * changes — sweeping the box across empty space, or growing it within the same
   * set of items, leaves the store untouched and so rebuilds no geometry. The
   * query itself is cheap; rebuilding the accent buffers per pixel would not be.
   */
  const refreshPreview = (current: MarqueeState | null): void => {
    const next: MarqueePreview | null = current
      ? { loopIds: collect(current), mode: marqueeMergeMode(current) }
      : null;

    previewStore.setState((previous) =>
      isSamePreview(previous, next) ? previous : next,
    );
  };

  const update = (change: (current: MarqueeState) => MarqueeState): void => {
    marqueeStore.setState((current) => (current ? change(current) : current));
    refreshPreview(marqueeStore.getState());
  };

  const clear = () => {
    marqueeStore.setState(() => null);
    previewStore.setState(() => null);
  };

  return {
    begin(screenPoint, modifiers) {
      marqueeStore.setState(() => beginMarquee(screenPoint, modifiers));
      previewStore.setState(() => null);
    },

    move(screenPoint) {
      update((current) => moveMarquee(current, screenPoint));
    },

    setAlt(isAltHeld) {
      // Alt flips the merge mode, so the preview has to restyle with it.
      update((current) => setMarqueeAlt(current, isAltHeld));
    },

    setSpace(isSpaceHeld) {
      update((current) => setMarqueeSpace(current, isSpaceHeld));
    },

    abort() {
      update(abortMarquee);
      clear();
    },

    isActive: () => marqueeStore.getState() !== null,

    commit() {
      const current = marqueeStore.getState();
      clear();

      if (!current) {
        return;
      }
      const caught: SelectionRef[] = collect(current).map((loopId) => ({
        kind: 'loop',
        loopId,
      }));

      const mode: MarqueeMergeMode = marqueeMergeMode(current);

      // A press that never travelled is a click, and the click handler owns it —
      // `collect` returns nothing for it, but a `replace` would still clear the
      // selection, so the gesture has to bow out before that.
      if (current.isAborted || !isMarqueeLive(current)) {
        return;
      }

      if (mode === 'replace') {
        // An empty replace box is how a user clears without hunting for blank canvas.
        selection.set(caught);
        return;
      }
      if (caught.length === 0) {
        // An add or subtract box that caught nothing leaves the selection alone,
        // rather than quietly clearing work the user just did.
        return;
      }
      if (mode === 'add') {
        selection.add(caught);
      } else {
        selection.remove(caught);
      }
    },
  };
};
